import { initializeApp } from 'firebase/app';
import {
    getAuth,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    sendPasswordResetEmail,
    updateEmail,
    updatePassword,
    sendEmailVerification,
    updateProfile
} from 'firebase/auth';

import {
    getFirestore,
    doc,
    getDoc,
    getDocs,
    setDoc,
    collection,
    query,
    where,
    updateDoc,
    orderBy,
    limit
} from 'firebase/firestore';

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_PROJECT_ID,
    storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_APP_ID,
    measurementId: process.env.REACT_APP_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

export const auth = getAuth();

export const db = getFirestore();

function CustomError(errorCode) {
    this.name = 'CustomError';
    this.message = '';
    this.code = errorCode;
}
  
CustomError.prototype = Object.create(Error.prototype);

const isValidUser = async (user) => {
    if (!user) return false;
    // if (!user.emailVerified) return false;
    try {
        const userDocRef = doc(db, 'users', user.uid);
        const userSnapshot = await getDoc(userDocRef);
        if (!userSnapshot.exists()) {
            return false;
        }
        return true;
    } catch (error) {
        //await logUserError('isValidUser', error.message, { userId: user.uid });
        console.log(error.message);
        return false;
    }
}

export const createUserDocumentFromAuth = async (userAuth, subscribeToEmails) => {
    if (!userAuth) return;
    try {
        const userDocRef = doc(db, 'users', userAuth.uid);
        const userSnapshot = await getDoc(userDocRef);
        if (!userSnapshot.exists()) {
            const { email } = userAuth;
            const createdAt = new Date();
            await setDoc(userDocRef, {
                email,
                createdAt,
                subscribeToEmails,
                displayName: email
            });
        }
        return userDocRef;
    } catch (error) {
        console.log(error.message);
    }
}

export const sendAuthUserEmailVerification = async (user) => {
    if (!user) return;
    //logUserEvent('sendAuthUserEmailVerification', { userId: user.uid })
    try {
        await sendEmailVerification(user);
    } catch (error) {
        //await logUserError('sendAuthUserEmailVerification', error.message, { userId: user.uid });
        console.log(error.message);
    }
}

export const createAuthUserWithEmailAndPassword = async (email, password) => {
    if (!email || !password) return;
    const { user } = await createUserWithEmailAndPassword(auth, email, password);
    return user;
}

export const signInAuthUserWithEmailAndPassword = async (email, password) => {
    if (!email || !password) return;
    const { user } = await signInWithEmailAndPassword(auth, email, password);
    if (user.displayName !== user.email){
        await updateProfile(user, { displayName:email });
    }
    return user;
}

export const onAuthStateChangedListener = (callback) => {
    if (!callback) return;
    onAuthStateChanged(auth, callback);
}

export const signOutUser = async () => {
    //logUserEvent('signOutUser', { userId: user.uid });
    try {
        await signOut(auth);
    } catch (error) {
        //await logUserError('signOutUser', error.message,{userId:user.uid});
        console.log(error.message);
    }
}

export const resetPassword = async (email) => {
    if (!email) return;
    await sendPasswordResetEmail(auth, email);
}

export const updateAuthUserEmail = async (currentUser, email) => {
    if (!currentUser || !email) return;
    // Check if anyone with email in users collection
    const usersCollection = collection(db, 'users');
    const usersWithEmailSnapshot = await getDocs(query(usersCollection, where("email", "==", email)));
    // Get the number of users with the same email
    const numUsers = usersWithEmailSnapshot.size;
    // If there are users with the same email, throw an error
    if (numUsers > 0){
        throw new CustomError('custom/email-already-in-use');
    }
    await updateEmail(currentUser, email);
    await updateUserData(currentUser, { email });
}

export const updateAuthUserPassword = async (password) => {
    if (!password) return;
    //logUserEvent('updateAuthUserPassword', { userId: auth.currentUser.uid });
    try {
        await updatePassword(auth.currentUser, password);
    } catch (error) {
        //await logUserError('updateAuthUserPassword', error.message, { userId: auth.currentUser.uid });
        console.log(error.message);
    }
}

export const getUserData = async (user) => {
    if (!user) return;
    try {
        const userDocRef = doc(db, 'users', user.uid);
        const userSnapshot = await getDoc(userDocRef);
        if (userSnapshot.exists()) {
            return userSnapshot.data();
        }
    } catch (error) {
        //await logUserError('getUserData', error.message,{userId:user.uid});
        console.log(error.message);
    }
}

export const updateUserData = async (user, data) => {
    if (!user || !data) return;
    //logUserEvent('updateUserData', { userId: user.uid, data })
    try {
        const userDocRef = doc(db, 'users', user.uid);
        await setDoc(userDocRef, data, { merge: true });
    } catch (error) {
        //await logUserError('updateUserData', error.message,{userId:user.uid,newData:data});
        console.log(error.message)
    }
}

export const fetchUserSurveys = async (user) => {
    if (!user) return;
    try {
        const validUser = await isValidUser(user);
        if (!validUser) return;
        const userSurveysCollectionRef = collection(db, 'users', user.uid, 'sandboxSurveyHistory');
        // Get the user's surveys (order by date and get 300 most recent)
        const userSurveysSnapshot = await getDocs(query(userSurveysCollectionRef, orderBy("date", "desc"), limit(300)));
        
        if (userSurveysSnapshot.length === 0) {
            return [];
        }

        const userSurveys = [];
        userSurveysSnapshot.forEach((doc) => {
            userSurveys.push({ ...doc.data(), id: doc.id, });
        }
        );
        // Sort by date
        userSurveys.sort((a, b) => b.date - a.date);
        
        return userSurveys;

    } catch (error) {
        //await logUserError('fetchUserSurveys', error.message, { userId: user.uid });
        console.log(error.message)
    }
}

export const giveUserFeedbackAboutSurvey = async (user, surveyId, resultsAreGood) => {
    if (!user || !surveyId) return;
    try {
        // check if valid user
        const validUser = await isValidUser(user);
        if (!validUser) return;
        const userSurveysCollectionRef = collection(db, 'users', user.uid, 'sandboxSurveyFeedback');
        const userSurveyDocRef = doc(userSurveysCollectionRef, surveyId);
        const userSurveySnapshot = await getDoc(userSurveyDocRef);
        // Get unix timestamp
        const date = new Date().getTime();
        const feedbackData = { date, resultsAreGood };
        if (userSurveySnapshot.exists()) {
            await updateDoc(userSurveyDocRef, feedbackData);
        } else {
            await setDoc(userSurveyDocRef, feedbackData);
        }
    } catch (error) {
        console.log(error.message);
    }
}

export const userRequestedHigherRate = async (currentUser) => {
    if (!currentUser) return;
    try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userSnapshot = await getDoc(userDocRef);
        if (userSnapshot.exists()) {
            // Update userData requestedHigherRate to true
            await updateDoc(userDocRef, { requestedHigherRate: true });
        }
    } catch (error) {
        console.log(error.message);
    }
}

export const updateUserEmailPreferences = async (currentUser, subscribeToEmails) => {
    if (!currentUser) return;
    try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userSnapshot = await getDoc(userDocRef);
        if (userSnapshot.exists()) {
            await updateDoc(userDocRef, { subscribeToEmails });
        }
    } catch (error) {
        console.log(error.message);
    }
}

export const getDataForSurvey = async (currentUser, surveyId) => {
    if (!currentUser || !surveyId) {
        throw new Error("Invalid input: currentUser and surveyId are required");
    }
    try {
        const validUser = await isValidUser(currentUser);
        if (!validUser) {
            throw new Error("User validation failed");
        }
        const surveyRef = collection(db, 'users', currentUser.uid, 'APIHistory');
        const surveySnapshot = await getDocs(query(surveyRef, where('surveyId', '==', surveyId)));
        const data = surveySnapshot.docs.map(doc => {
            const { 
                APICallID, 
                participantId, 
                checksReturnedToUser = null, 
                questions, 
                finalStates, 
                aliasModel = 'alias-v01',
                fingerprintChecks = {},
                duplicateFingerprintParticipantId = undefined,
                responseGroups = {}, 
                extraData = {},
                changeHistory = {},
                effortRatings = {},
            } = doc.data();

            let parsedChecksReturned;
            try {
                parsedChecksReturned = JSON.parse(checksReturnedToUser);
            } catch (error) {
                console.error("Failed to parse checksReturnedToUser:", error);
                parsedChecksReturned = null;
            }

            return {
                ...extraData,
                participantId, 
                checksReturnedToUser: parsedChecksReturned,
                questions,
                finalStates,
                aliasModel,
                surveyId,
                APICallID,
                responseGroups,
                fingerprintChecks,
                duplicateFingerprintParticipantId,
                hasExtraData: Object.keys(extraData).length > 0,
                changeHistory,
                effortRatings,
            };
        });
        return data;
    } catch (error) {
        console.error("Failed to get data for survey:", error.message);
        throw error; // Rethrow or handle as needed by your application context
    }
};

export const getQuestionHistoryDataWithCallId = async (currentUser, callId, questionId) => {
    if (!currentUser || !callId) {
        throw new Error("Invalid input: currentUser and callId are required");
    }
    try {
        // Get only first document with the callId
        const surveyRef = collection(db, 'users', currentUser.uid, 'APIHistory');
        const surveySnapshot = await getDocs(query(surveyRef, where('APICallID', '==', callId), limit(1)));
        // Return doc.data() if it exists
        if (surveySnapshot.docs.length === 0) {
            throw new Error("No data found for callId");
        }
        const doc = surveySnapshot.docs[0];
        const { changeHistory, questions } = doc.data();
        if (questionId){
            return {
                histories: {[questionId]: changeHistory[questionId]},
                questions: {[questionId]: questions[questionId]},
            };
        }
        return {
            histories: changeHistory,
            questions,
        };
        
    } catch (error) {
        console.error("Failed to get data for survey:", error.message);
        throw error; // Rethrow or handle as needed by your application context
    }
};